import React from "react";
import { motion } from "framer-motion";
import {
  FiClock,
  FiPaperclip,
  FiMessageCircle,
  FiCheckSquare,
} from "react-icons/fi";
import "./TaskCard.css";

const TaskCard = ({ task, onClick, accentColor = "#6366f1" }) => {
  const priorityConfig = {
    low: { color: "#10b981", label: "Low", bg: "#ecfdf5" },
    normal: { color: "#6366f1", label: "Normal", bg: "#eef2ff" },
    high: { color: "#f59e0b", label: "High", bg: "#fffbeb" },
    urgent: { color: "#ef4444", label: "Urgent", bg: "#fef2f2" },
  };

  const priority = priorityConfig[task.priority] || priorityConfig.normal;

  const formatDueDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { text: `${Math.abs(diffDays)}d overdue`, isOverdue: true };
    } else if (diffDays === 0) {
      return { text: "Today", isUrgent: true };
    } else if (diffDays === 1) {
      return { text: "Tomorrow", isUrgent: true };
    } else if (diffDays < 7) {
      return { text: `${diffDays}d left`, isUrgent: false };
    } else {
      return {
        text: date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        isUrgent: false,
      };
    }
  };

  const dueDate = task.dueDate ? formatDueDate(task.dueDate) : null;
  const completedSubtasks =
    task.subtasks?.filter((s) => s.completed).length || 0;
  const totalSubtasks = task.subtasks?.length || 0;
  const hasSubtasks = totalSubtasks > 0;
  const hasAttachments = task.attachments?.length > 0;
  const hasComments = task.comments?.length > 0;

  return (
    <motion.div
      className="rtc-card"
      onClick={onClick}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
    >
      {/* Priority Strip */}
      <div
        className="rtc-priority-strip"
        style={{ background: priority.color }}
      />

      {/* Card Content */}
      <div className="rtc-content">
        {/* Header */}
        <div className="rtc-header">
          <span
            className="rtc-priority-badge"
            style={{ color: priority.color, background: priority.bg }}
          >
            {priority.label}
          </span>
          {task.isOverdue && <span className="rtc-overdue-badge">Overdue</span>}
        </div>

        {/* Title */}
        <h4 className="rtc-title">{task.title}</h4>

        {/* Description */}
        {task.description && (
          <p className="rtc-description">
            {task.description.length > 80
              ? task.description.substring(0, 80) + "..."
              : task.description}
          </p>
        )}

        {/* Progress */}
        {task.progress > 0 && (
          <div className="rtc-progress-wrapper">
            <div className="rtc-progress-header">
              <span className="rtc-progress-label">Progress</span>
              <span className="rtc-progress-value">{task.progress}%</span>
            </div>
            <div className="rtc-progress-track">
              <motion.div
                className="rtc-progress-fill"
                initial={{ width: 0 }}
                animate={{ width: `${task.progress}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                style={{ background: accentColor }}
              />
            </div>
          </div>
        )}

        {/* Subtasks */}
        {hasSubtasks && (
          <div className="rtc-subtasks">
            <FiCheckSquare size={14} />
            <span>
              {completedSubtasks}/{totalSubtasks}
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="rtc-footer">
          {/* Assignee */}
          <div className="rtc-assignee">
            <div className="rtc-avatar" style={{ background: accentColor }}>
              {task.assignedTo?.profilePhotoUrl ? (
                <img
                  src={task.assignedTo.profilePhotoUrl}
                  alt={task.assignedTo.name}
                  className="rtc-avatar-img"
                />
              ) : (
                (task.assignedTo?.name || "U").charAt(0).toUpperCase()
              )}
            </div>
            <span className="rtc-assignee-name">
              {task.assignedTo?.name || "Unassigned"}
            </span>
          </div>

          {/* Meta Info */}
          <div className="rtc-meta">
            {dueDate && (
              <span
                className={`rtc-due ${
                  dueDate.isOverdue
                    ? "rtc-due--overdue"
                    : dueDate.isUrgent
                    ? "rtc-due--urgent"
                    : ""
                }`}
              >
                <FiClock size={12} />
                {dueDate.text}
              </span>
            )}
            {hasAttachments && (
              <span className="rtc-meta-item">
                <FiPaperclip size={12} />
                {task.attachments.length}
              </span>
            )}
            {hasComments && (
              <span className="rtc-meta-item">
                <FiMessageCircle size={12} />
                {task.comments.length}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TaskCard;
